import React from 'react';
import { MapPin, Phone, MessageCircle, Truck } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { contactInfo, locations } from '../data/mock';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import LocationMap from './LocationMap';

const ContactForm = () => {
  const { t } = useLanguage();

  const handleWhatsAppClick = () => {
    const message = `Bonjour! Je souhaite réserver un véhicule CleanRide Rental à Bora Bora.`;
    window.open(`https://wa.me/${contactInfo.whatsapp}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleCallClick = () => {
    window.open(`tel:${contactInfo.phone}`, '_self');
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-white to-gray-50">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t.contact.title}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t.contact.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* WhatsApp Contact Card */}
          <div className="space-y-6">
            <Card className="shadow-2xl border-2 border-[#0077FF] bg-gradient-to-br from-white to-blue-50">
              <CardHeader className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="bg-[#25D366] p-6 rounded-full shadow-lg">
                    <MessageCircle className="w-16 h-16 text-white" />
                  </div>
                </div>
                <CardTitle className="text-3xl font-bold text-gray-900">
                  {t.contact.whatsappButton}
                </CardTitle>
                <CardDescription className="text-lg">
                  Réponse rapide garantie !
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button
                  onClick={handleWhatsAppClick}
                  size="lg"
                  className="w-full bg-[#25D366] hover:bg-[#20BA5A] text-white font-bold py-8 text-xl shadow-xl"
                >
                  <MessageCircle className="w-8 h-8 mr-3" />
                  {contactInfo.phone}
                </Button>
                
                <div className="text-center">
                  <p className="text-gray-600 mb-3">ou</p>
                  <Button
                    onClick={handleCallClick}
                    variant="outline"
                    size="lg"
                    className="w-full border-2 border-[#0077FF] text-[#0077FF] hover:bg-[#0077FF] hover:text-white font-semibold py-6 text-lg"
                  >
                    <Phone className="w-6 h-6 mr-2" />
                    {t.contact.callUs}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Pickup Locations */}
            <Card className="shadow-lg border-2 border-gray-200">
              <CardHeader>
                <CardTitle className="text-2xl font-bold text-gray-900 flex items-center">
                  <MapPin className="w-6 h-6 mr-2 text-[#0077FF]" />
                  {t.contact.locations.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                  <div className="flex items-start space-x-3">
                    <MapPin className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                    <div>
                      <div className="font-semibold text-gray-900">{t.contact.locations.vaitape}</div>
                      <div className="text-sm text-gray-600">Retrait gratuit</div>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center text-sm font-semibold text-[#FF8C00] mb-2">
                    <Truck className="w-5 h-5 mr-2" />
                    {t.contact.locations.deliveryNote}
                  </div>
                  
                  <div className="flex items-start space-x-3 pl-4 border-l-2 border-gray-300">
                    <MapPin className="w-5 h-5 text-[#0077FF] mt-1 flex-shrink-0" />
                    <div>
                      <div className="font-semibold text-gray-900">{t.contact.locations.matira}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3 pl-4 border-l-2 border-gray-300">
                    <MapPin className="w-5 h-5 text-[#0077FF] mt-1 flex-shrink-0" />
                    <div>
                      <div className="font-semibold text-gray-900">{t.contact.locations.anau}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3 pl-4 border-l-2 border-gray-300">
                    <MapPin className="w-5 h-5 text-[#0077FF] mt-1 flex-shrink-0" />
                    <div>
                      <div className="font-semibold text-gray-900">{t.contact.locations.faanui}</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Map */}
          <div className="rounded-2xl overflow-hidden shadow-xl border-2 border-gray-200 h-[600px]">
            <LocationMap />
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
